import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type Language = 'en' | 'th' | 'zh' | 'ja';

export const languageNames: Record<Language, string> = {
  en: 'English',
  th: 'ไทย',
  zh: '中文',
  ja: '日本語'
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  languageNames: typeof languageNames;
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome',
    'dashboard.metrics': 'Metrics',
    'dashboard.revenue': 'Revenue',
    'dashboard.transactions': 'Transactions',
    'dashboard.tenants': 'Tenants',
    'dashboard.health': 'System Health',
    'dashboard.latency': 'Latency',
    'dashboard.payments': 'Payments',
    'dashboard.pending': 'Pending',
    'dashboard.completed': 'Completed',
    'dashboard.failed': 'Failed',
    'dashboard.refresh': 'Refresh',
    'dashboard.updating': 'Updating...',
    'dashboard.editMode': 'Edit Mode',
    'dashboard.done': 'Done',
    'dashboard.resetLayout': 'Reset Layout',
    'dashboard.systemMonitor': 'System Monitor',
    'dashboard.businessMetrics': 'Business Metrics',
    'dashboard.aiSystems': 'AI/Agent Systems',
    'dashboard.adminTools': 'Admin Tools',
    'dashboard.systemHealth': 'System Health',
    'dashboard.quickActions': 'Admin Quick Actions',
    'dashboard.securityAlerts': 'Security Alerts',
    'dashboard.deploymentStatus': 'Deployment Status',
    'dashboard.backendLatency': 'Backend Latency',
    'dashboard.performanceCharts': 'Performance Charts',
    'dashboard.resourceMonitoring': 'Resource Monitoring',
    'dashboard.aiProcesses': 'AI Processes',
  },
  th: {
    'dashboard.title': 'แผงควบคุม',
    'dashboard.welcome': 'ยินดีต้อนรับ',
    'dashboard.metrics': 'เมตริก',
    'dashboard.revenue': 'รายได้',
    'dashboard.transactions': 'ธุรกรรม',
    'dashboard.tenants': 'ผู้เช่า',
    'dashboard.health': 'สุขภาพระบบ',
    'dashboard.latency': 'ความล่าช้า',
    'dashboard.payments': 'การชำระเงิน',
    'dashboard.pending': 'รอดำเนินการ',
    'dashboard.completed': 'เสร็จสิ้น',
    'dashboard.failed': 'ล้มเหลว',
    'dashboard.refresh': 'รีเฟรช',
    'dashboard.updating': 'กำลังอัปเดต...',
    'dashboard.editMode': 'โหมดแก้ไข',
    'dashboard.done': 'เสร็จสิ้น',
    'dashboard.resetLayout': 'รีเซ็ต prejudice',
    'dashboard.systemMonitor': ' Montana',
    'dashboard.businessMetrics': 'เมตริกธุรกิจ',
    'dashboard.aiSystems': 'ระบบ AI/Agent',
    'dashboard.adminTools': 'เครื่องมือ Admin',
    'dashboard.systemHealth': 'สุขภาพระบบ',
    'dashboard.quickActions': 'การกระทำด่วน',
    'dashboard.securityAlerts': 'การแจ้งเตือนความปลอดภัย',
    'dashboard.deploymentStatus': 'สถานะการติดตั้ง',
    'dashboard.backendLatency': 'ความล่าช้าของ Backend',
    'dashboard.performanceCharts': 'กราฟประสิทธิภาพ',
    'dashboard.locationTracking': 'การติดตามตำแหน่ง',
    'dashboard.resourceMonitoring': 'การตรวจสอบทรัพยากร',
    'dashboard.aiProcesses': 'กระบวนการ AI',
  },
  zh: {
    'dashboard.title': '仪表板',
    'dashboard.welcome': '欢迎',
    'dashboard.metrics': '指标',
    'dashboard.revenue': '收入',
    'dashboard.transactions': '交易',
    'dashboard.tenants': '租户',
    'dashboard.health': '系统健康',
    'dashboard.latency': '延迟',
    'dashboard.payments': '支付',
    'dashboard.pending': '待处理',
    'dashboard.completed': '已完成',
    'dashboard.failed': '失败',
    'dashboard.refresh': '刷新',
    'dashboard.updating': '更新中...',
    'dashboard.editMode': '编辑模式',
    'dashboard.done': '完成',
    'dashboard.resetLayout': '重置布局',
    'dashboard.systemMonitor': '系统监控',
    'dashboard.businessMetrics': '业务指标',
    'dashboard.aiSystems': 'AI/代理系统',
    'dashboard.adminTools': '管理工具',
    'dashboard.systemHealth': '系统健康',
    'dashboard.quickActions': '快速操作',
    'dashboard.securityAlerts': '安全警报',
    'dashboard.deploymentStatus': '部署状态',
    'dashboard.backendLatency': '后端延迟',
    'dashboard.performanceCharts': '性能图表',
    'dashboard.locationTracking': '位置追踪',
    'dashboard.resourceMonitoring': '资源监控',
    'dashboard.aiProcesses': 'AI 进程',
  },
  ja: {
    'dashboard.title': 'ダッシュボード',
    'dashboard.welcome': 'ようこそ',
    'dashboard.metrics': 'メトリクス',
    'dashboard.revenue': '収益',
    'dashboard.transactions': '取引',
    'dashboard.tenants': 'テナント',
    'dashboard.health': 'システム正常性',
    'dashboard.latency': '遅延',
    'dashboard.payments': '支払い',
    'dashboard.pending': '保留中',
    'dashboard.completed': '完了',
    'dashboard.failed': '失敗',
    'dashboard.refresh': '更新',
    'dashboard.updating': '更新中...',
    'dashboard.editMode': '編集モード',
    'dashboard.done': '完了',
    'dashboard.resetLayout': 'レイアウトをリセット',
    'dashboard.systemMonitor': 'システム監視',
    'dashboard.businessMetrics': 'ビジネス指標',
    'dashboard.aiSystems': 'AI/エージェントシステム',
    'dashboard.adminTools': '管理ツール',
    'dashboard.systemHealth': 'システム正常性',
    'dashboard.quickActions': 'クイックアクション',
    'dashboard.securityAlerts': 'セキュリティアラート',
    'dashboard.deploymentStatus': 'デプロイメントステータス',
    'dashboard.backendLatency': 'バックエンド遅延',
    'dashboard.performanceCharts': 'パフォーマンスチャート',
    'dashboard.locationTracking': '位置追跡',
    'dashboard.resourceMonitoring': 'リソース監視',
    'dashboard.aiProcesses': 'AI プロセス',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('language');
    return (stored as Language) || 'en';
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  }, []);

  const t = useCallback((key: string): string => {
    return translations[language][key as keyof typeof translations[Language]] || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languageNames }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
